'use strict';

const { fetchFeaturedBlock } = require('./adformBidsRequest');
const adformConstants = require('./adformConstants');
const adformElementEvents = require('./adformElementEvents');
const { generateFeaturedBlockHtml } = require('./helpers/adformContentBuilderHelpers');

const ADFORM_FEATURED_BLOCK_WRAPPER_JS_CLASS = 'js-adform-featured-block-wrapper';

const initPopOver = require('progamers_core/components/initPopOver');
const { productTileTrackingInViewport } = require('progamers_core/components/datalayer');

/**
 * Get all feature block wrappers (page designer component where we insert html based on adform request)
 * @return {NodeList} - feature block wrappers
 */
function getFeaturedBlockWrappers() {
    return document.querySelectorAll(`.${ADFORM_FEATURED_BLOCK_WRAPPER_JS_CLASS}`);
}

/**
 * Fetch products from our BE based on adform bids (product_ids...).
 * On success, generates and renders "featured block" with these products.
 * @param {Object[]} bid - adform bid
 * @param {Element} wrapper - element where we render "featured block"
 * @param {string} bannerPosition - if "featured block" banner should be "left" or "right"
 */
function ajaxFetchFeaturedBlockProducts(bid, wrapper, bannerPosition) {
    let ajaxUrl = window.ajaxSearchUpdateGridUrl;
    if (!ajaxUrl || !wrapper || !bannerPosition) {
        return;
    }

    let productIdsPipeSeparator = bid?.descriptionAsset?.product_ids?.join('|')?.replaceAll('"', '');
    if (!productIdsPipeSeparator) {
        return;
    }

    fetch(`${ajaxUrl}?pid=${productIdsPipeSeparator}`)
        .then((response) => {
            if (response.ok) {
                return response.text();
            }

            return false;
        })
        .then((html) => {
            if (!html) {
                return;
            }

            const parser = new DOMParser();
            const responseDom = parser.parseFromString(html, 'text/html');
            const responseProductTiles = responseDom.querySelectorAll('.product-tiles');

            if (!responseProductTiles.length) {
                return;
            }

            let productTilesHtml = Array.from(responseProductTiles).map(
                tile => tile.outerHTML
            ).join('');

            const wrapperIndex = Array.from(getFeaturedBlockWrappers()).indexOf(wrapper) + 1;
            const dataTileContainerId = `adform-featured-block-${wrapperIndex}`;

            const featuredBlockHtml = generateFeaturedBlockHtml(bid, productTilesHtml, bannerPosition, dataTileContainerId);
            if (!featuredBlockHtml) {
                return;
            }

            wrapper.innerHTML = featuredBlockHtml;

            adformElementEvents.initInViewportObserver(wrapper, bid);
            adformElementEvents.initFeaturedBlockClickEvent(wrapper, bid.linkURL);

            productTileTrackingInViewport(wrapper);

            // todo: move "js product init" logic to shared place and reuse in search.js too
            initPopOver();

            document.body.dispatchEvent(new CustomEvent('product:tileRendered', { detail: wrapper, bubbles: true }));
        });
}

/**
 * Init events that make adform request to get data needed for featured block.
 */
function initMakeRequestEvents() {
    const tagIDs = adformConstants.FEATURED_BLOCK_TAG_IDS;
    const bannerPositions = adformConstants.FEATURED_BLOCK_BANNER_POSITIONS;

    document.addEventListener('DOMContentLoaded', () => {
        getFeaturedBlockWrappers().forEach((wrapper, index) => {
            const tagId = Array.isArray(tagIDs) ? tagIDs[index] : null;
            const bannerPosition = Array.isArray(bannerPositions) ? bannerPositions[index] : null;
            if (tagId && bannerPosition) {
                fetchFeaturedBlock(tagId, wrapper, bannerPosition);
            }
        });
    }, { once: true }); // context: cookiebot may fire event again when partially accepted (since cookiebot not 1st script in head)
}

/**
 * Init events that wait for adform response to get processed:
 * - case bids exist => create featured block
 * - case no bids => nothing happens
 */
function initResponseHandlingEvents() {
    document.addEventListener(adformConstants.FEATURED_BLOCK_BIDS_READY_CUSTOM_EVENT_NAME, function (e) {
        const bids = e.detail?.bids;
        const additionalEventData = e.detail?.additionalEventData;
        const wrapper = additionalEventData?.wrapper;
        const bannerPosition = additionalEventData?.bannerPosition;
        if (!wrapper || !bannerPosition || !bids.length) {
            return;
        }

        const bid = bids[0];
        ajaxFetchFeaturedBlockProducts(bid, wrapper, bannerPosition);
    });

    document.addEventListener(adformConstants.FEATURED_BLOCK_NO_BIDS_CUSTOM_EVENT_NAME, function () {});
}

/**
 * Init adform fetch request and handling response (bids or no bids).
 *
 * Important:
 * initResponseHandlingEvents() must be called before initMakeRequestEvents()
 * => otherwise request could dispatch event (e.g. early return "no-bids" event when request not valid)
 *    that can't be processed (response handler event not initialized yet)
 */
function initRequestLifecycleEvents() {
    initResponseHandlingEvents();
    initMakeRequestEvents();
}

module.exports = {
    init: function () {
        if (!window.shouldFetchAdformHomepageFeaturedBlocks
        ) {
            return;
        }

        initRequestLifecycleEvents();
    }
};
